import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { EarlyBirdCountdown } from "./EarlyBirdCountdown";
import { TICKETCORNER_URL } from "../config";
import { trackInitiateCheckout } from "../analytics";

// Tamanho do adjetivo ("PORTUGUÊS"...) por idioma, em % da largura do bloco
// (mesma proporção da antiga imagem: 9 chars = 15.2cqi).
const ADJ_CQI = { pt: 15.2, en: 13.7, de: 9.1, fr: 15.2, it: 13.7 };

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const { t, lang } = useLanguage();

    return (
        <section id="topo" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
            <motion.div
                style={{ y: bgY }}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
            >
                <img
                    src="/images/rafael-pacheco-bg.webp"
                    alt="Rafael Pacheco com guitarra portuguesa num restaurante à luz de velas"
                    className="w-full h-[115%] object-cover object-[58%_25%] lg:h-auto"
                    data-testid="hero-background"
                />
                <div className="absolute bottom-0 left-0 right-0 h-[45vh] bg-gradient-to-t from-[#070503] via-[#070503]/55 to-transparent" data-testid="hero-bottom-fade" />
            </motion.div>

            <div className="hero-content relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-10 pt-24 pb-14 sm:pb-16 flex-1 flex flex-col">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="headline-scale w-full max-w-[280px] sm:max-w-[310px] lg:max-w-[340px] mt-14 [@media(min-width:1024px)_and_(max-height:820px)]:max-w-[300px]" data-testid="hero-headline">
                        <p className="font-caps gold-text uppercase text-[9cqi] leading-[1.15] tracking-[0.08em] whitespace-pre-line" data-testid="hero-headline-top">
                            {t.hero.headlineTop}
                        </p>
                        <p className="font-display font-medium text-[#efe6d6] text-[33cqi] leading-[0.95] tracking-[0.02em] mt-[2cqi]" data-testid="hero-headline-fado">
                            FADO
                        </p>
                        <p className="font-display font-medium text-[#efe6d6] leading-[1.05] tracking-[0.04em] mt-[0.1cqi]" style={{ fontSize: `${ADJ_CQI[lang] || 15.2}cqi` }} data-testid="hero-headline-adj">
                            {t.hero.headlineAdj}
                        </p>
                        <img src="/images/ornamento-oficial.webp" alt="" aria-hidden="true" className="mt-[3cqi] mb-[2.2cqi] w-[82%]" data-testid="hero-headline-flourish" />
                        <p className="font-display italic gold-text text-[7.2cqi] leading-[1.3] whitespace-pre-line" data-testid="hero-headline-italic">
                            {t.hero.headlineItalico}
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1, duration: 1 }}
                    className="mt-auto pt-4"
                >
                    <div className="lg:flex lg:items-end lg:justify-between lg:gap-10">
                        <div className="max-w-xl">
                            <div className="grid grid-cols-1 sm:grid-cols-2 border border-[#c9a24b]/50 bg-[#070503]/60 backdrop-blur-sm sm:divide-x sm:divide-[#c9a24b]/40" data-testid="hero-info">
                            <div className="px-5 py-4" data-testid="hero-quando">
                                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f]">{t.hero.quando}</p>
                                <p className="font-display font-medium gold-text text-xl sm:text-2xl leading-tight mt-1.5">{t.hero.data}</p>
                                <p className="font-display font-light text-sm text-[#efe6d6]/65 mt-0.5">{t.hero.hora}</p>
                            </div>
                            <div className="px-5 py-4 border-t sm:border-t-0 border-[#c9a24b]/40" data-testid="hero-onde">
                                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f]">{t.hero.onde}</p>
                                <p className="font-display font-medium gold-text text-xl sm:text-2xl leading-tight mt-1.5">{t.hero.local}</p>
                                <p className="font-display font-light text-sm text-[#efe6d6]/65 mt-0.5">{t.hero.morada}</p>
                            </div>
                            </div>
                            <p className="mt-4 hidden lg:block font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b]/80 leading-relaxed" data-testid="hero-urgencia-desktop">
                                {t.hero.urgencia}
                            </p>
                        </div>
                        {/* Desktop: CTA + countdown + parceiro na coluna direita, BASE alinhada com a célula Quando/Onde */}
                        <div className="hidden lg:flex flex-col items-end" data-testid="hero-cta-right">
                            <Link
                                to="/bilhetes"
                                data-testid="comprar-bilhetes-button-desktop"
                                className="font-caps text-sm tracking-[0.25em] uppercase bg-[#5e1224] text-[#efe6d6] border border-[#c9a24b]/50 px-10 py-3 hover:bg-[#7a1a30] hover:border-[#c9a24b] transition-colors duration-400 inline-block"
                            >
                                {t.hero.comprar}
                            </Link>
                            <EarlyBirdCountdown className="mt-3" compact />
                            <div className="mt-3 flex items-center justify-end gap-3" data-testid="hero-parceiro-desktop">
                                <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#c9a24b]/70 text-right leading-relaxed" data-testid="hero-parceiro-label-desktop">
                                    {t.ticketcorner.label}
                                </p>
                                <a href={TICKETCORNER_URL} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid="hero-ticketcorner-link-desktop" aria-label={`Ticketcorner — ${t.ticketcorner.label}`} onClick={() => trackInitiateCheckout(TICKETCORNER_URL)}>
                                    <img loading="lazy" decoding="async" src="/images/ticketcorner-blue.png" alt={`Ticketcorner — ${t.ticketcorner.label}`}
                                        className="w-[clamp(120px,9vw,150px)] h-auto" data-testid="hero-ticketcorner-logo-desktop" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 lg:hidden" data-testid="hero-cta-row">
                        <Link
                            to="/bilhetes"
                            data-testid="comprar-bilhetes-button"
                            className="font-caps text-sm tracking-[0.25em] uppercase bg-[#5e1224] text-[#efe6d6] border border-[#c9a24b]/50 px-10 py-4 hover:bg-[#7a1a30] hover:border-[#c9a24b] transition-colors duration-400 inline-block"
                        >
                            {t.hero.comprar}
                        </Link>
                    </div>
                    <div className="lg:hidden" data-testid="hero-countdown-row">
                        <EarlyBirdCountdown />
                    </div>
                    <p className="mt-5 lg:hidden font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b]/80 leading-relaxed" data-testid="hero-urgencia">
                        {t.hero.urgencia}
                    </p>
                    <p className="mt-6 font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b]/70 lg:hidden" data-testid="hero-parceiro-label">
                        {t.ticketcorner.label}
                    </p>
                    <div className="mt-4 w-[240px] max-w-full text-center lg:hidden" data-testid="hero-ticketcorner">
                        <a href={TICKETCORNER_URL} target="_blank" rel="noopener noreferrer" className="inline-block" data-testid="hero-ticketcorner-link" aria-label={`Ticketcorner — ${t.ticketcorner.label}`} onClick={() => trackInitiateCheckout(TICKETCORNER_URL)}>
                            <img loading="lazy" decoding="async" src="/images/ticketcorner-blue.png" alt={`Ticketcorner — ${t.ticketcorner.label}`}
                                className="inline-block w-[clamp(150px,11vw,170px)] h-auto" data-testid="hero-ticketcorner-logo" />
                        </a>
                    </div>
                    <Link
                        to="/artistas/rafael-pacheco"
                        data-testid="saiba-mais-mobile"
                        className="xl:hidden mt-8 font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 bg-[#070503]/40 px-6 py-3 inline-block"
                    >
                        {t.hero.saibaMais}
                    </Link>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="hidden xl:block absolute z-10 left-[84.2%] top-[calc(24.2vw+14px)] -translate-x-1/2"
            >
                <Link
                    to="/artistas/rafael-pacheco"
                    data-testid="saiba-mais-button"
                    className="font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 bg-[#070503]/40 backdrop-blur-sm px-6 py-2.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400 inline-block"
                >
                    {t.hero.saibaMais}
                </Link>
            </motion.div>
        </section>
    );
};
